import { Link } from '@tanstack/react-router';
import { ExternalLink, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { AdminAuction } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';

import { bpsToPercent } from '../schemas/auction-form-schema';

import { LotThumb } from './lot-thumb';

export function AuctionLotCard({ storeId, auction }: { storeId: string; auction: AdminAuction }) {
  const { t } = useTranslation('auctions');
  const { lot } = auction;
  return (
    <Card className="gap-3" data-tour="auctions.detail.lot">
      <CardHeader>
        <CardTitle>{t('lot.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-start gap-3">
        <LotThumb lot={lot} size={64} />
        <div className="flex min-w-0 flex-col gap-1 text-sm">
          <Link
            to="/s/$storeId/catalog/$storeProductId"
            params={{ storeId, storeProductId: lot.storeProductId }}
            className="font-medium hover:underline"
          >
            {lot.title}
          </Link>
          <span className="text-muted-foreground">{lot.variantTitle ?? t('lot.noVariant')}</span>
          {lot.sku ? <code className="text-muted-foreground text-xs">{lot.sku}</code> : null}
          <span className="text-muted-foreground text-xs">{t('lot.quantity')}</span>
        </div>
      </CardContent>
    </Card>
  );
}

/** Who won, at what price, and the unpaid order staff mark paid when the money arrives. */
export function AuctionOutcomeCard({
  storeId,
  auction,
}: {
  storeId: string;
  auction: AdminAuction;
}) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  if (auction.status === 'cancelled') {
    return (
      <Card className="gap-3">
        <CardHeader>
          <CardTitle>{t('outcomeCard.title')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p>{t('outcomeCard.cancelled')}</p>
          {auction.cancelReason ? (
            <p className="text-muted-foreground mt-1">
              {t('outcomeCard.reason', { reason: auction.cancelReason })}
            </p>
          ) : null}
        </CardContent>
      </Card>
    );
  }
  const person = auction.winner ?? auction.leader;
  return (
    <Card className="gap-3" data-tour="auctions.detail.outcome">
      <CardHeader>
        <CardTitle>
          {auction.status === 'ended' ? t('outcomeCard.title') : t('outcomeCard.leaderTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        {person ? (
          <p>
            <span className="font-medium">{person.name ?? person.email}</span>{' '}
            <span className="text-muted-foreground">
              {t('bidder.number', { number: person.bidderNumber })} · {person.email}
            </span>
          </p>
        ) : (
          <p className="text-muted-foreground">{t('outcomeCard.noBids')}</p>
        )}
        {auction.status === 'live' && auction.leader ? (
          <p className="text-muted-foreground">
            {t('outcomeCard.leaderMax', { price: format.money(auction.leader.maxAmount) })}
          </p>
        ) : null}
        {auction.outcome === 'sold' && auction.soldVia ? (
          <p className="text-muted-foreground">{t(`outcomeCard.soldVia.${auction.soldVia}`)}</p>
        ) : null}
        {auction.outcome === 'sold' && auction.hammerPrice ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-muted-foreground">{t('outcomeCard.hammer')}</dt>
            <dd className="text-right tabular-nums">{format.money(auction.hammerPrice)}</dd>
            {auction.buyerPremium ? (
              <>
                <dt className="text-muted-foreground">{t('outcomeCard.premium')}</dt>
                <dd className="text-right tabular-nums">{format.money(auction.buyerPremium)}</dd>
              </>
            ) : null}
          </dl>
        ) : null}
        {auction.outcome === 'no_sale' ? (
          <p className="text-muted-foreground">
            {auction.leader ? t('outcomeCard.reserveNotMet') : t('outcomeCard.noBids')}
          </p>
        ) : null}
        {auction.orderId ? (
          <Button asChild variant="outline" size="sm" className="self-start">
            <Link
              to="/s/$storeId/orders/$orderId"
              params={{ storeId, orderId: auction.orderId }}
              data-tour="auctions.detail.order"
            >
              <ExternalLink aria-hidden />
              {t('outcomeCard.openOrder')}
            </Link>
          </Button>
        ) : null}
        {auction.orderId ? (
          <p className="text-muted-foreground text-xs">{t('outcomeCard.unpaidHint')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function AuctionSettingsCard({
  auction,
  canEdit,
  onEdit,
}: {
  auction: AdminAuction;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation('auctions');
  const format = useFormat();
  const m = (amount: number) => format.money({ amount, currency: auction.currency });
  return (
    <Card className="gap-3" data-tour="auctions.detail.settings">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>{t('settings.title')}</CardTitle>
        {canEdit ? (
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil aria-hidden />
            {t('actions.edit')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
          <dt className="text-muted-foreground">{t('fields.startingPrice')}</dt>
          <dd className="text-right tabular-nums">{format.money(auction.startingPrice)}</dd>
          <dt className="text-muted-foreground">{t('fields.reservePrice')}</dt>
          <dd className="text-right tabular-nums">
            {auction.reservePrice ? format.money(auction.reservePrice) : t('reserve.none')}
          </dd>
          <dt className="text-muted-foreground">{t('fields.buyNowPrice')}</dt>
          <dd className="text-right tabular-nums">
            {auction.buyNowPrice ? format.money(auction.buyNowPrice) : t('settings.noBuyNow')}
          </dd>
          <dt className="text-muted-foreground">{t('fields.startsAt')}</dt>
          <dd className="text-right" title={format.utc(auction.startsAt)}>
            {format.dateTime(auction.startsAt)}
          </dd>
          <dt className="text-muted-foreground">{t('settings.scheduledEnd')}</dt>
          <dd className="text-right" title={format.utc(auction.originalEndsAt)}>
            {format.dateTime(auction.originalEndsAt)}
          </dd>
          <dt className="text-muted-foreground">{t('fields.antiSnipe')}</dt>
          <dd className="text-right">
            {auction.antiSnipeMinutes > 0
              ? t('settings.antiSnipe', { count: auction.antiSnipeMinutes })
              : t('settings.antiSnipeOff')}
          </dd>
          <dt className="text-muted-foreground">{t('fields.premium')}</dt>
          <dd className="text-right">
            {auction.buyerPremiumBps === null
              ? t('settings.noPremium')
              : `${bpsToPercent(auction.buyerPremiumBps)}%`}
          </dd>
        </dl>
        <div>
          <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
            {t('fields.increments')}
          </p>
          <ul className="flex flex-col gap-0.5" aria-label={t('fields.increments')}>
            {auction.incrementTable.map((tier) => (
              <li key={tier.fromCents} className="flex justify-between tabular-nums">
                <span>{t('settings.tierFrom', { price: m(tier.fromCents) })}</span>
                <span>{t('settings.tierStep', { price: m(tier.incrementCents) })}</span>
              </li>
            ))}
          </ul>
        </div>
        {!canEdit && auction.status !== 'draft' && auction.status !== 'scheduled' ? (
          <p className="text-muted-foreground text-xs">{t('settings.locked')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
